import { useEffect, useState } from 'react'
import api from '../../../helpers/apiRequester'
import { Banner, Button, Card, Switch, useToast } from 'ui'
import { apiErrorMessage, NotificationPreferences, UserProfile } from './shared'
import s from './styles.module.scss'

interface Props {
    profile: UserProfile
    onChange: (patch: Partial<UserProfile>) => void
}

const OPTIONS: { key: keyof NotificationPreferences; label: string; description: string }[] = [
    {
        key: 'notify_new_sign_in',
        label: 'New sign-in alerts',
        description: "Email me when my account signs in from a device or browser that hasn't been seen before.",
    },
    {
        key: 'notify_security',
        label: 'Security changes',
        description: 'Confirmations when my password, two-factor setup or sign-in email changes, or when "Sign out everywhere" is used.',
    },
    {
        key: 'notify_product_updates',
        label: 'Dashboard updates',
        description: 'Occasional emails about new features and changes to the dashboard.',
    },
]

/** Which account emails the user receives. Stored on dashboard_users. */
export const NotificationsCard = ({ profile, onChange }: Props) => {
    const { toast } = useToast()
    const [prefs, setPrefs] = useState<NotificationPreferences>(profile.notifications)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        setPrefs(profile.notifications)
    }, [profile.notifications])

    const dirty = OPTIONS.some(({ key }) => prefs[key] !== profile.notifications[key])

    const save = async () => {
        if (!dirty || saving) return
        setSaving(true)
        setError(null)
        const changes: Partial<NotificationPreferences> = {}
        OPTIONS.forEach(({ key }) => {
            if (prefs[key] !== profile.notifications[key]) changes[key] = prefs[key]
        })
        try {
            const response = await api({ url: '/profile-notifications', data: changes })
            if (!response.data?.success) throw new Error(response.data?.error || 'Save failed')
            onChange({ notifications: response.data.notifications })
            toast({ title: 'Preferences saved', tone: 'good' })
        } catch (err: any) {
            setError(apiErrorMessage(err, 'Could not save your preferences.'))
        } finally {
            setSaving(false)
        }
    }

    return (
        <Card title="Email notifications" subtitle={`Sent to ${profile.email}. Security-critical messages about your sign-in email are always sent.`}>
            <div className={s.form}>
                {error && (
                    <Banner tone="bad" title="Couldn't save">
                        {error}
                    </Banner>
                )}
                <ul className={s.prefList}>
                    {OPTIONS.map((option) => (
                        <li key={option.key} className={s.pref}>
                            <div className={s.prefText}>
                                <strong>{option.label}</strong>
                                <span>{option.description}</span>
                            </div>
                            <Switch
                                aria-label={option.label}
                                checked={prefs[option.key]}
                                onChange={(e) => setPrefs((prev) => ({ ...prev, [option.key]: e.target.checked }))}
                                disabled={saving}
                            />
                        </li>
                    ))}
                </ul>
                <div className={s.formActions}>
                    {dirty && <p className={s.formNote}>You have unsaved changes.</p>}
                    <Button type="button" onClick={() => setPrefs(profile.notifications)} disabled={!dirty || saving}>
                        Reset
                    </Button>
                    <Button type="button" variant="primary" onClick={() => void save()} loading={saving} disabled={!dirty}>
                        Save preferences
                    </Button>
                </div>
            </div>
        </Card>
    )
}
